/**
 * SubmitForReviewDialog (Story 24.2 — Epic 24: Report Approval Workflow)
 *
 * Centered confirmation modal (UI directive §4.4) for submitting / resubmitting
 * a report into the approval workflow. Kept mounted + gated by `open` so the
 * AnimatePresence exit animation plays (§4.6). The verdict comment lives on the
 * reviewer side (24-4), so submit is a plain confirm here.
 */

import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@provenleap/ui";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import type { ReportListItem } from "./ReportTableView";

interface SubmitForReviewDialogProps {
  report: ReportListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
  error: string | null;
}

export function SubmitForReviewDialog({
  report,
  open,
  onOpenChange,
  onConfirm,
  isPending,
  error,
}: SubmitForReviewDialogProps) {
  function handleOpenChange(next: boolean) {
    if (!next && isPending) return;
    onOpenChange(next);
  }

  const isResubmit =
    report?.approvalStatus === "changes_requested" ||
    report?.approvalStatus === "withdrawn";
  const verb = isResubmit ? "Resubmit" : "Submit";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="submit-review-dialog"
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {verb} for review?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {report
                      ? `"${report.name}" will be marked Pending review and the assigned reviewer (or your team admins) will be notified.`
                      : "This report will be marked Pending review and reviewers will be notified."}
                  </DialogPrimitive.Description>
                </div>
                {error && (
                  <p className="mt-4 text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={onConfirm}
                    disabled={isPending}
                    data-testid="confirm-submit-review-btn"
                  >
                    <PaperPlaneTilt size={16} className="mr-1" />
                    {isPending ? `${verb}ting…` : `${verb} for review`}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
